import { blue } from "@ant-design/colors"
import { Card, DatePicker, Divider, Flex, Form, Skeleton } from "antd"
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from "react"
import { AuthenticationContext } from "../../../../AuthenticationContext"
import { ProjectStatuses, ProjectTypes, WorkareaItemType } from "../../../../common/constants"
import { CatalogTemplateWithItems } from "../../../../domain/catalogTemplate"
import { usePartialEditWorkareaGroupMutation, useWorkareaGroupsQuery, WorkareaGroupMutationData } from "../../../../domain/workareas/groupsQueries"
import { useUpdateWorkareaMutation, WorkareaMutationData } from "../../../../domain/workareas/workareaQueries"
import ChangeOnBlurInput from "../../../common/ChangeOnBlurInput"
import VSpace from "../../../common/VSpace"
import CustomLaborRateSelector from "../../../Items/CustomLaborRateSelector/CustomLaborRateSelector"
import { ItemsContext } from "../../../Items/ItemsRoot"
import WorkareaItems from "../../../Items/WorkareaItems"
import KeepFinalPriceModal from "../../KeepFinalPriceModal"
import SaveAsTemplateButton from "../../SaveAsTemplateModal/SaveAsTemplateButton"
import JobPlanContext from "../JobPlanContext"
import { arePricingFieldsLockedForGroup, getAlreadyInvoicedAmountForWorkarea } from "../List/workareaGroupsListModel"
import ApprovalStateSwitch from "./ApprovalStateSwitch"
import CatalogItemHandler from "./CatalogItemHandler"
import DrawerHeader from "./DrawerHeader"
import * as GroupEditorModel from './GroupEditor/groupEditorModel'
import QboSalesTaxSelector from "./QboSalesTaxSelector"
import RecurringWorkareaCard from "./RecurringWorkareaCard"
import CostCodeSelector from "./WorkareaEditor/CostCodeSelector"
import DivisionSelector from "./WorkareaEditor/DivisionSelector"
import ProposalDescriptionAttachments from "./ProposalDescriptionAttachments"
import DescriptionCard from "./DescriptionCard"
import NotesEditor from "./WorkareaEditor/NotesEditor"
import PricingTypeSelector from "./WorkareaEditor/PricingTypeSelector"
import StatusSelector from "./WorkareaEditor/StatusSelector"
import SyncRecurringWorkareas from "./WorkareaEditor/SyncRecurringWorkareas"
import * as EditorModel from "./workareaEditorModel"
import ShowTaxInFinalPriceControl, { isTaxDisplayedForPricingType } from "./WorkareaEditor/ShowTaxInFinalPriceControl"
import RefreshPricingButton from "../../../Items/RefreshPricingButton"
import TemplateProductionRatesButton from "../../../Items/TemplateProductionRates/TemplateProductionRatesButton"
import TotalsCard from "./WorkareaEditor/TotalsCard"
import TaxRateSelector from "../../../invoicing/TaxRates/TaxRateSelector"

const WorkareaEditor = () => {
  const { currentUser } = useContext(AuthenticationContext)
  const { currentSelection, transactions, setIsTotalsCalculating: setContextTotalsCalculating, invoicingSystem } = useContext(JobPlanContext)
  const { jobData: { jobId, jobType }, workareaSummaries } = useContext(ItemsContext)
  const [selectedItemsTab, setSelectedItemsTab] = useState<WorkareaItemType>('estimated')
  const [isTotalsCalculating, setIsTotalsCalculating] = useState(false)
  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)
  const updateWorkareaMutation = useUpdateWorkareaMutation()
  const partialEditWorkareaGroupMutation = usePartialEditWorkareaGroupMutation()
  const workareaGroups = workareaGroupsQuery.data ?? []
  const selectedWorkarea = workareaGroups.flatMap(g => g.workareas).find(w => w.id === currentSelection?.workareaId)
  const summary = workareaSummaries.find(x => x.workareaId === currentSelection?.workareaId)

  const isAutosaving = updateWorkareaMutation.isLoading || partialEditWorkareaGroupMutation.isLoading
  const isSaved = updateWorkareaMutation.isSuccess || partialEditWorkareaGroupMutation.isSuccess

  useEffect(() => {
    setContextTotalsCalculating(isTotalsCalculating)
  }, [isTotalsCalculating, setContextTotalsCalculating])

  const [cachedQuotedPrice, setCachedQuotedPrice] = useState<number | undefined>()
  useEffect(() => setCachedQuotedPrice(EditorModel.getQuotedPriceToCache(selectedWorkarea, workareaGroups)), [selectedWorkarea, workareaGroups])

  const [approvedPriceToKeep, setApprovedPriceToKeep] = useState<number | undefined>()

  const [forceCustomerAndInvoiceNotesUpdateCounter, setForceCustomerAndInvoiceNotesUpdateCounter] = useState(0)

  const handleWorkareaUpdate = (data: WorkareaMutationData) => {
    if (data.isValidUpdate) {
      updateWorkareaMutation.mutate(data)
    }
  }

  const finalPriceChangeHandler = useMemo(() => (value: number | null) => {
    if (selectedWorkarea) {
      handleWorkareaUpdate(EditorModel.updateWorkareaFinalPrice(selectedWorkarea, value, summary?.estimatedPrice))
    }
  }, [selectedWorkarea, summary?.estimatedPrice])

  const handleGroupUpdate = (data: WorkareaGroupMutationData) => {
    if (data.isValidUpdate) {
      partialEditWorkareaGroupMutation.mutate(data)
    }
  }

  if (selectedWorkarea === undefined) {
    return <Skeleton active paragraph={{ rows: 13 }} style={{ margin: '20px' }} />
  }

  const groupData = EditorModel.getGroupData(workareaGroups, selectedWorkarea)
  const pricingFieldsLocked = arePricingFieldsLockedForGroup(groupData.group, transactions)
  const alreadyInvoicedAmount = getAlreadyInvoicedAmountForWorkarea(selectedWorkarea.id, transactions)

  const oneThirdWidthStyle = { minWidth: '300px' }
  const hasChildWorkareas = EditorModel.hasChildWorkareas(selectedWorkarea, workareaGroups)

  const showQboSalesTaxSelector = currentUser?.isTenantConnectedToQuickBooks === true
    && currentUser?.tenantQuickbooksCompanyCountryCode !== 'US'
  const showTaxRateSelector = GroupEditorModel.shouldShowTaxRateSelector(invoicingSystem, groupData.group)
  const showSalesTaxSection = isTaxDisplayedForPricingType(groupData.group.pricingType) || showQboSalesTaxSelector || showTaxRateSelector

  const showRecurringWorkareaCard = jobType === ProjectTypes.Maintenance &&
    !selectedWorkarea.masterWorkareaId &&
    !hasChildWorkareas


  const handleIsOnHoldChange = (value: boolean) => {
    handleGroupUpdate(GroupEditorModel.updateGroupIsOnHold(groupData.group, value, []))
    handleWorkareaUpdate(EditorModel.updateWorkareaIsOnHold(selectedWorkarea, value))

    if (value && EditorModel.shouldUpdateQuotedPrice(selectedWorkarea, { ...groupData.group, isApproved: false }, summary?.estimatedPrice ?? 0)) {
      setApprovedPriceToKeep(selectedWorkarea.quotedPrice)
    }
  }

  const handleIsApprovedChange = (value: boolean) => {
    handleGroupUpdate(GroupEditorModel.updateGroupIsApproved(groupData.group, value, []))
    handleWorkareaUpdate(EditorModel.updateWorkareaIsApproved(selectedWorkarea, value))

    if (!value && EditorModel.shouldUpdateQuotedPrice(selectedWorkarea, { ...groupData.group, isApproved: false }, summary?.estimatedPrice ?? 0)) {
      setApprovedPriceToKeep(selectedWorkarea.quotedPrice)
    }
  }

  const handleTemplateNotes = (workareaId: number, template: CatalogTemplateWithItems) => {
    if (workareaId !== selectedWorkarea.id) {
      console.error(`Template notes received for workarea ${workareaId} but selected workarea is ${selectedWorkarea.id}`)
      return
    }

    const update = EditorModel.applyTemplateNotes(selectedWorkarea, template)
    handleWorkareaUpdate(update)

    selectedWorkarea.customerAndInvoiceNotes = update.optimisticUpdate.customerAndInvoiceNotes
    selectedWorkarea.notes = update.optimisticUpdate.notes
    selectedWorkarea.invoiceDescription = update.optimisticUpdate.invoiceDescription
    setForceCustomerAndInvoiceNotesUpdateCounter(forceCustomerAndInvoiceNotesUpdateCounter + 1)
  }

  return <div>
    <CatalogItemHandler onTemplateAdd={handleTemplateNotes} workareaId={selectedWorkarea.id} focusedView={selectedItemsTab === 'estimated' ? 'workarea-estimated' : 'workarea-actual'} />
    <DrawerHeader isAutosaving={isAutosaving} isSaved={isSaved} />
    <div style={{ height: 'calc(100vh - 116px)', overflowY: 'auto' }}>
      <div style={{ padding: '24px' }}>
        <Flex vertical gap="large">
          <Card
            title="Description"
          >
            <Form layout="vertical">
              <Flex gap="large" align="start" wrap="wrap">
                <Form.Item label="This work area is on hold">
                  <ApprovalStateSwitch
                    value={groupData.group.isOnHold}
                    onChange={handleIsOnHoldChange}
                    canEditGroupFields={groupData.canEditGroupFields}
                    hasInvoices={pricingFieldsLocked}
                    isApproved={groupData.group.isApproved}
                  />
                </Form.Item>
                <Form.Item label="This work area is approved">
                  <ApprovalStateSwitch
                    value={groupData.group.isApproved}
                    onChange={handleIsApprovedChange}
                    canEditGroupFields={groupData.canEditGroupFields}
                    hasInvoices={pricingFieldsLocked}
                    isApproved={groupData.group.isApproved}
                  />
                </Form.Item>
              </Flex>
              <Flex gap="large" align="start" wrap="wrap">
                <Form.Item label="Workarea Name" style={oneThirdWidthStyle}>
                  <ChangeOnBlurInput
                    value={selectedWorkarea.name}
                    highlightWhenEmpty
                    onBlurChange={x => handleWorkareaUpdate(EditorModel.updateWorkareaName(selectedWorkarea, x))}
                    maxLength={75}
                  />
                </Form.Item>
                <Form.Item label="Status" style={oneThirdWidthStyle}>
                  <StatusSelector
                    status={selectedWorkarea.status}
                    onChange={status => handleWorkareaUpdate(EditorModel.updateWorkareaStatus(selectedWorkarea, status))}
                  />
                </Form.Item>
                <Form.Item label="Completed Date" style={oneThirdWidthStyle}>
                  <DatePicker
                    format="MM/DD/YYYY"
                    value={selectedWorkarea.completedDate ? dayjs(selectedWorkarea.completedDate) : undefined}
                    onChange={date => handleWorkareaUpdate(EditorModel.updateWorkareaCompletedDate(selectedWorkarea, date?.toISOString()))}
                    disabled={selectedWorkarea.status !== ProjectStatuses.Completed}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Flex>

              <Flex gap="large" align="start" wrap="wrap">
                <Form.Item label="Division" style={oneThirdWidthStyle}>
                  <DivisionSelector
                    divisionId={selectedWorkarea.divisionId}
                    onChange={divisionId => handleWorkareaUpdate(EditorModel.updateWorkareaDivision(selectedWorkarea, divisionId))}
                  />
                </Form.Item>
                <Form.Item label="Pricing Type" style={oneThirdWidthStyle}>
                  <VSpace>
                    <PricingTypeSelector
                      value={groupData.group.pricingType}
                      onChange={(pricingType) => handleGroupUpdate(GroupEditorModel.updateGroupPricingType(groupData.group, pricingType))}
                      canEditGroupFields={groupData.canEditGroupFields}
                      isPricingLocked={pricingFieldsLocked}
                      excludePerVisit={jobType !== ProjectTypes.Maintenance}
                      isApproved={groupData.group.isApproved}
                    />
                  </VSpace>
                </Form.Item>
                <Form.Item label="Cost Code" style={oneThirdWidthStyle}>
                  <CostCodeSelector
                    costCodeId={selectedWorkarea.costCodeId}
                    onChange={costCodeId => handleWorkareaUpdate(EditorModel.updateWorkareaCostCode(selectedWorkarea, costCodeId))}
                  />
                </Form.Item>
              </Flex>
              
              <Form.Item label="Custom Labor Rate" style={{width: '300px'}}>
                <CustomLaborRateSelector
                  workareaId={selectedWorkarea.id}
                  selectedCatalogItemId={selectedWorkarea.customLaborRateItemId}
                  onChange={customLaborRateItemId => handleWorkareaUpdate(EditorModel.updateWorkareaCustomLaborRateId(selectedWorkarea, customLaborRateItemId))}
                />
              </Form.Item>

              {showSalesTaxSection && <Form.Item label="Sales Tax">
                <Flex gap={2} align="center" wrap="wrap">
                  <ShowTaxInFinalPriceControl
                    pricingType={groupData.group.pricingType}
                    isEditingGroupLevel={false}
                    canEditGroupFields={groupData.canEditGroupFields}
                    showTaxInFinalPrice={groupData.group.showTaxInFinalPrice}
                    onChange={value => handleGroupUpdate(GroupEditorModel.updateShowTaxInFinalPrice(groupData.group, value))}
                  />
                  {isTaxDisplayedForPricingType(groupData.group.pricingType) && (showQboSalesTaxSelector || showTaxRateSelector) && <Divider type="vertical" />}
                  {showQboSalesTaxSelector && <div style={{ width: '250px' }}>
                    <QboSalesTaxSelector
                      selectedSalesTaxId={groupData?.group.qboSalesTaxId}
                      canEditGroupFields={groupData.canEditGroupFields}
                      onChange={x => handleGroupUpdate(GroupEditorModel.updateGroupQboSalesTaxId(groupData.group, x))}
                    />
                  </div>}
                  {showTaxRateSelector && <div style={{ minWidth: '250px' }}>
                    <TaxRateSelector
                      invoicingSystem={invoicingSystem}
                      jobId={jobId}
                      selectedTaxRateId={groupData.group.taxRateId}
                      canEditGroupFields={groupData.canEditGroupFields}
                      onChange={x => handleGroupUpdate(GroupEditorModel.updateGroupTaxRateId(groupData.group, x ?? null))}
                    />
                  </div>}
                </Flex>
              </Form.Item>}
            </Form>
          </Card>
          <TotalsCard
            workareaId={selectedWorkarea.id}
            group={groupData.group}
            selectedWorkarea={selectedWorkarea}
            cachedQuotedPrice={cachedQuotedPrice}
            isSaving={isAutosaving}
            alreadyInvoicedAmount={alreadyInvoicedAmount}
            onUpdateCachedQuotedPrice={setCachedQuotedPrice}
            onCustomizationChange={finalPriceChangeHandler}
            onCalculatingChange={setIsTotalsCalculating}
          />
          {showRecurringWorkareaCard && <RecurringWorkareaCard />}
          <Card
            title="Items"
            tabList={[
              { key: 'estimated', tab: 'Estimated' },
              { key: 'actual', tab: 'Actual' },
            ]}
            activeTabKey={selectedItemsTab}
            onTabChange={key => setSelectedItemsTab(key as WorkareaItemType)}
          >
            <VSpace>
              <WorkareaItems
                workareaId={selectedWorkarea.id}
                type={selectedItemsTab}
                quotedPrice={cachedQuotedPrice}
              />
              <Flex justify="end" gap={8}>
                <RefreshPricingButton workareaId={selectedWorkarea.id} />
                {hasChildWorkareas && <SyncRecurringWorkareas workarea={selectedWorkarea} />}
                <TemplateProductionRatesButton
                  workareaId={selectedWorkarea.id}
                  disabled={selectedItemsTab !== 'estimated'}
                />
                <SaveAsTemplateButton
                  workareaId={selectedWorkarea.id}
                  disabled={selectedItemsTab !== 'estimated'}
                  type="default"
                  colorPrimary={blue.primary}
                  addIcon
                />
              </Flex>
            </VSpace>
          </Card>

          <DescriptionCard
            owner={groupData.group.isHidden ? 'workarea' : 'groupedWorkarea'}
            proposal={<>
              <NotesEditor
                workareaOrGroupId={currentSelection?.workareaId}
                notes={selectedWorkarea.customerAndInvoiceNotes}
                onChange={notes => handleWorkareaUpdate(EditorModel.updateWorkareaCustomerAndInvoiceNotes(selectedWorkarea, notes))}
                forceUpdateCounter={forceCustomerAndInvoiceNotesUpdateCounter}
              />

              <ProposalDescriptionAttachments workareaId={selectedWorkarea.id} />
            </>}
            invoice={<NotesEditor
              workareaOrGroupId={currentSelection?.workareaId}
              notes={selectedWorkarea.invoiceDescription ?? ''}
              onChange={description => handleWorkareaUpdate(EditorModel.updateWorkareaInvoiceDescription(selectedWorkarea, description))}
              forceUpdateCounter={forceCustomerAndInvoiceNotesUpdateCounter}
            />}
          />

          <Card title="Internal Notes">
            <NotesEditor
              workareaOrGroupId={currentSelection?.workareaId}
              notes={selectedWorkarea.notes}
              onChange={notes => handleWorkareaUpdate(EditorModel.updateWorkareaNotes(selectedWorkarea, notes))}
              forceUpdateCounter={forceCustomerAndInvoiceNotesUpdateCounter}
            />
          </Card>
        </Flex>
      </div>
    </div>

    <KeepFinalPriceModal
      isOpen={approvedPriceToKeep !== undefined}
      currentFinalPrice={approvedPriceToKeep ?? 0}
      estimatedPrice={summary?.estimatedPrice ?? 0}
      setCustomizedFinalPrice={(value) => {
        handleWorkareaUpdate(EditorModel.updateWorkareaFinalPrice(selectedWorkarea, value, summary?.estimatedPrice))
        setApprovedPriceToKeep(undefined)
      }}
      alreadyInvoiced={alreadyInvoicedAmount}
      pricingType={groupData.group.pricingType}
    />
  </div>
}

export default WorkareaEditor
